import React from 'react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';

interface AttackVolumeChartProps {
    data?: { date: string; count: number }[] | any[];
    isLoading?: boolean;
}

const AttackVolumeChart: React.FC<AttackVolumeChartProps> = ({ data = [], isLoading }) => {

    if (isLoading) {
        return (
            <div className="w-full h-full flex items-center justify-center text-[#39FF14] font-mono text-xs animate-pulse">
                Telemetry syncing...
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="w-full h-full flex items-center justify-center text-slate-600 font-mono text-xs border border-dashed border-[#1A1D24] rounded-lg">
                No volume telemetry available.
            </div>
        );
    }

    // Format the API date (e.g., "2026-02-23") into a clean "02-23" for the X-axis
    const formatXAxis = (tickItem: string) => {
        const parts = tickItem.split('-');
        if (parts.length === 3) {
            return `${parts[1]}-${parts[2]}`;
        }
        return tickItem;
    };

    // Custom Tooltip matching your dark-mode aesthetic
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-[#0A0B0E]/95 backdrop-blur-md border border-[#1A1D24] p-3 rounded-lg shadow-[0_0_15px_rgba(0,0,0,0.5)] font-mono z-50 relative">
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">{label}</p>
                    <div className="flex items-end gap-2">
                        <span className="text-xl font-bold text-white leading-none">{payload[0].value}</span>
                        <span className="text-[10px] text-[#39FF14] uppercase tracking-wide">Attacks</span>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="absolute inset-0">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                    data={data}
                    margin={{ top: 20, right: 0, left: -25, bottom: 0 }}
                >
                    <defs>
                        {/* 1. The Diagonal "Light Beam" Gradient */}
                        {/* x1=1, y1=0 to x2=0, y2=1 forces the gradient diagonally from top-right to bottom-left */}
                        <linearGradient id="diagonalFade" x1="1" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#39FF14" stopOpacity={0.6} />
                            <stop offset="40%" stopColor="#39FF14" stopOpacity={0.2} />
                            <stop offset="80%" stopColor="#39FF14" stopOpacity={0} />
                        </linearGradient>

                        {/* 2. The exact Figma Neon Glow Filter */}
                        <filter id="neonGlow" x="-20%" y="-20%" width="140%" height="140%">
                            <feGaussianBlur stdDeviation="4" result="blur" />
                            <feMerge>
                                <feMergeNode in="blur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>

                    <CartesianGrid
                        vertical={false}
                        strokeDasharray="4 4"
                        stroke="#1A1D24"
                    />

                    <XAxis
                        dataKey="date"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#475569', fontSize: 10, fontFamily: 'monospace' }}
                        tickFormatter={formatXAxis}
                        dy={10}
                    />

                    <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#475569', fontSize: 10, fontFamily: 'monospace' }}
                        dx={-10}
                    />

                    <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ stroke: '#1A1D24', strokeWidth: 1, strokeDasharray: '4 4' }}
                    />

                    <Area
                        type="monotone"
                        dataKey="count"
                        stroke="#39FF14" // Exact Hex Applied
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#diagonalFade)" // Diagonal cut applied
                        style={{ filter: 'url(#neonGlow)' }}
                        activeDot={{ r: 6, fill: '#0A0B0E', stroke: '#39FF14', strokeWidth: 2 }}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

export default AttackVolumeChart;